/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import {
  InventoryProduct,
  useCancelProducts,
  useStockInventoryQuery,
  fetchAllProductsForExport,
  fetchAllProductIds,
} from 'src/services/inventory';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import { t } from 'i18next';
import styles from './styles/Inventory.module.scss';
import { useNavigationItems } from '../hooks/useNavigationItems';
import { DataView } from '../components/ui/TableOverview/DataView';
import NoDataPlaceholder from '../components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { useDataViewController } from '../hooks/useDataViewController';
import { SearchModels } from '../components/ui/TableOverview/utils/SearchFieldRegistry';
import Icon from '../components/ui/Icon/Icon';
import { OverlayNavProvider } from '../providers/OverlayNavProvider';
import { useQuickFilterQuery } from '../services/utility';
import { showSpinnerToast, hideSpinnerToast } from '../utils/spinnerToaster';
import { ButtonToneProps } from '../components/ui/Button/Button';
import { useConfirmProductBulk } from '../services/productsNew';
import { Status } from '../utils/general';
import { PortalProductType } from '../components/layouts/SelectProducts/SelectProducts';

export function InventorySatusBadge({ status }: { status: InventoryProduct['status'] }) {
  const badgeType: Record<string, BadgeType> = {
    draft: BadgeType.Neutral2,
    confirmed: BadgeType.Successful,
  };

  return (
    <Badge
      className="max-w-min normal-case"
      label={t(`product.${status}`)}
      expanded
      type={badgeType[status.toLowerCase()] ?? BadgeType.Neutral}
    />
  );
}

function Inventory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: cancelProducts } = useCancelProducts();
  const { data: quickFilters } = useQuickFilterQuery('product.product');
  const { mutateAsync: confirmProducts } = useConfirmProductBulk();

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('warehouse');

  const columnHelper = createColumnHelper<InventoryProduct>();

  const columns = [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      enableHiding: false,
      minSize: 200,
      meta: {
        label: t('general.name'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const row = info.row.original;
        return (
          <div className="flex items-center w-fit gap-x-6">
            <div className="flex flex-col leading-tight">
              <span className="typo-text-14--bold text-full">{row.name}</span>
              <span className="typo-text-12 text-faded">{row.article_number}</span>
            </div>
            {!row.standard_price && (
              <span className="inline-flex text-faded">
                <Icon name="info" width={20} height={20} hoverText={t('warning.no_purchase_price')} />
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.product_id, {
      id: 'id',
      size: 100,
      meta: {
        label: t('general.id'),
      },
      cell: info => <span className={classNames(styles.cellTitle)}> {info.getValue()} </span>,
      header: ({ column }) => <span className={classNames(styles.cellSub)}>{column.columnDef.meta?.label}</span>,
    }),
    columnHelper.accessor(row => row.barcode, {
      id: 'barcode',
      meta: {
        label: t('general.barcode'),
      },
      cell: info =>
        info.row.original.barcode?.length && info.row.original.barcode.length > 0 ? (
          <span className={classNames(styles.cellTitle, styles.code)}> {info.getValue()} </span>
        ) : null,
      header: ({ column }) => <span className={classNames(styles.cellSub)}>{column.columnDef.meta?.label}</span>,
    }),
    columnHelper.accessor(row => row.on_stock, {
      id: 'qty_available',
      enableSorting: true,
      meta: {
        label: t('legend.on_stock'),
      },
      cell: info => <span className={classNames(styles.cellTitle, styles.centered)}> {info.getValue()} </span>,
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
    }),
    columnHelper.accessor('incoming', {
      id: 'incoming_qty',
      enableSorting: true,
      meta: {
        label: t('legend.incoming'),
      },
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
      cell: info => (
        <span className={classNames(styles.cellTitle, styles.centered)}>
          {' '}
          {info.row.original.portal_product_type === PortalProductType.Set ? '-' : info.getValue()}{' '}
        </span>
      ),
    }),
    columnHelper.accessor('outgoing', {
      id: 'outgoing_qty',
      enableSorting: true,
      meta: {
        label: t('legend.outgoing'),
      },
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
      cell: info => (
        <span className={classNames(styles.cellTitle, styles.centered)}>
          {' '}
          {info.row.original.portal_product_type === PortalProductType.Set ? '-' : info.getValue()}{' '}
        </span>
      ),
    }),
    columnHelper.accessor('forecast_stock', {
      id: 'virtual_available',
      enableSorting: true,
      meta: {
        label: t('legend.forecasted'),
      },
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
      cell: info => {
        const row = info.row.original;
        if (row.portal_product_type === PortalProductType.Set) {
          return <span className={classNames(styles.cellTitle, styles.centered)}> - </span>;
        }
        return (
          <div className="w-full text-center">
            <span className={classNames(styles.cellTitle, 'relative inline-block')}>
              {info.getValue()}
              {row.forecast_stock < 0 && (
                <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-8">
                  <Icon
                    name="warning_filled"
                    width={24}
                    height={24}
                    hoverText={t('warning.forecasted_stock_negative')}
                  />
                </span>
              )}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor('not_reserved', {
      id: 'not_reserved_qty',
      enableSorting: true,
      meta: {
        label: t('legend.not_reserved'),
      },
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
      cell: info => {
        const value = info.getValue();
        return (
          <span
            className={classNames(styles.cellTitle, styles.centered, {
              '!status-error-full': value < 0,
            })}>
            {' '}
            {value}{' '}
          </span>
        );
      },
    }),
    columnHelper.accessor('not_available', {
      id: 'not_available_qty',
      enableSorting: true,
      meta: {
        label: t('legend.not_available'),
      },
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
      cell: info => (
        <span className={classNames(styles.cellTitle, styles.centered)}>
          {' '}
          {info.row.original.portal_product_type === PortalProductType.Set ? '-' : info.getValue()}{' '}
        </span>
      ),
    }),
    columnHelper.accessor(row => row.portal_product_type, {
      id: 'portal_product_type',
      enableSorting: true,
      meta: {
        label: t('general.type'),
      },
      cell: info => (
        <span className={classNames(styles.cellTitle, styles.centered)}> {t(`product.${info.getValue()}`)} </span>
      ),
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
    }),
    columnHelper.accessor(row => row.status, {
      id: 'status',
      enableSorting: true,
      meta: {
        label: t('general.status'),
      },
      cell: info => (
        <div className="flex">
          <span className="typo-text-14 text-full capitalize">
            <InventorySatusBadge status={info.getValue()} />
          </span>
        </div>
        // <span className={classNames(styles.cellTitle, styles.centered)}> {t(`product.${info.getValue()}`)} </span>
      ),
      header: ({ column }) => (
        <span className={classNames(styles.cellSub, styles.centered)}>{column.columnDef.meta?.label}</span>
      ),
    }),
    columnHelper.accessor('category', {
      enableSorting: true,
      meta: {
        label: t('general.category'),
      },
      header: ({ column }) => <span className={classNames(styles.cellSub)}>{column.columnDef.meta?.label}</span>,
      cell: info => <span className={classNames(styles.cellTitle)}> {info.getValue()} </span>,
    }),
  ];

  const handleExportExcel = async () => {
    try {
      showSpinnerToast(t('general.exporting'));
      const response = await fetchAllProductsForExport();
      const products = Array.isArray(response) ? response : (response as any).items ?? [];

      const headers = {
        articleNumber: 'Artikelnummer',
        productName: 'Produktname',
        barcode: 'Barcode',
        length: 'Länge [cm]',
        width: 'Breite [cm]',
        height: 'Höhe [cm]',
        weight: 'Gewicht [kg]',
        hsCode: 'Zolltarifnummer',
        countryOfOrigin: 'Ursprungsland',
        sellingPrice: 'Verkaufspreis',
        purchasePrice: 'Einkaufspreis (Optional)',
      };
      const worksheetData = products.map((p: any) => ({
        [headers.articleNumber]: p.article_number ?? '',
        [headers.productName]: p.name ?? '',
        [headers.barcode]: p.barcode ?? '',
        [headers.length]: p.length ?? 0,
        [headers.width]: p.width ?? 0,
        [headers.height]: p.height ?? 0,
        [headers.weight]: p.weight ?? 0,
        [headers.hsCode]: p.hs_code ?? '',
        [headers.countryOfOrigin]: p.country_of_origin ?? '',
        [headers.sellingPrice]: p.list_price ?? 0,
        [headers.purchasePrice]: p.standard_price ?? 0,
      }));
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, t('navbar.inventory'));
      XLSX.writeFile(workbook, 'inventory_export.xlsx');
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      hideSpinnerToast();
    }
  };

  const controller = useDataViewController();
  const [entityIds, setEntityIds] = useState<(string | number)[]>([]);

  return (
    <OverlayNavProvider entityIds={entityIds} basePath="/warehouse/inventory">
      <div className={classNames(styles.root)}>
        <DataView<InventoryProduct>
          onDataLoaded={items => setEntityIds(items.map(i => i.product_id))}
          controller={controller}
          modelKey={SearchModels.Product}
          filterModelId="product.product"
          headline={t('navbar.inventory')}
          emptyState={<NoDataPlaceholder headline={t('general.empty_inventory')} />}
          initialHiddenColumns={['id', 'category']}
          withSaveFilters
          allowBulkActions={accessRights?.read_write}
          bulkIdField="product_id"
          fetchAllBulkIds={fetchAllProductIds}
          tabs={[
            { label: t('general.all'), key: 'all', value: quickFilters?.map(q => q.value).reduce((a, b) => a + b, 0) },
            ...(quickFilters ?? []),
          ]}
          getDomainForTab={(tabKey: string, index: number) =>
            index === 0 ? '[]' : `[("portal_product_type", "=", "${tabKey}")]`
          }
          actions={[
            {
              title: t('product.export'),
              icon: 'download',
              showInToolbar: true,
              toolbarTone: ButtonToneProps.SECONDARY,
              onExecute: async () => {
                await handleExportExcel();
              },
            },
            {
              title: t('general.create'),
              menuItems: [
                {
                  label: t('product_inventory.create_cta'),
                  onClick: () => {
                    navigate('/warehouse/inventory/create');
                  },
                },
                {
                  label: t('product_inventory.import_cta'),
                  onClick: () => navigate('/warehouse/inventory/import'),
                },
              ],
              icon: 'plus',
              showInToolbar: accessRights?.read_write,
            },
            {
              title: t('general.view_details'),
              icon: 'edit',
              isRowClickAction: true,
              onExecute: (selected: any) => {
                if (selected.length > 0) {
                  navigate(`/warehouse/inventory/${selected[0].product_id}`);
                }
              },
              showInContextMenu: true,
            },
            {
              title: t('general.delete_product'),
              titlePlural: t('general.delete_products'),
              icon: 'close',
              showInContextMenu: !accessRights?.read_write,
              bulkSupport: true,
              isDisabled: (selected: InventoryProduct[]) =>
                selected.some(s => s.status.toLowerCase() !== Status.DRAFT.toLowerCase()),
              disabledHint: t('general.delete_product_disabled_hint'),
              toast: {
                renderSuccessMessage: () => t('success.delete_product_success'),
                renderErrorMessage: () => t('errors.delete_product_failed'),
              },
              onExecute: async (selected: any) => {
                const ids = selected.map((s: any) => s.product_id).filter((id: any) => id !== undefined && id !== null);
                cancelProducts({
                  params: {
                    path: {
                      model: 'product.product',
                    },
                  },
                  body: ids,
                });
              },
              confirmSettings: {
                renderTitle: (selected: any) => {
                  if (selected.length > 1) {
                    return t('general.delete_products');
                  }
                  return t('general.delete_product');
                },
                renderContent: (selected: any) => {
                  if (selected.length > 1) {
                    return t('general.delete_products_confirmation');
                  }
                  return t('general.delete_product_confirmation');
                },
                confirmLabel: t('general.confirm'),
                cancelLabel: t('general.cancel'),
              },
            },
            {
              title: t('general.confirm_product_action'),
              titlePlural: t('general.confirm_products_action'),
              icon: 'checkmark',
              showInContextMenu: accessRights?.read_write,
              bulkSupport: true,
              isDisabled: (selected: InventoryProduct[]) => {
                const confirmable = selected.map(s => s.status.toLowerCase() === Status.DRAFT.toLowerCase());
                return confirmable.some(c => !c);
              },
              disabledHint: t('general.confirm_product_disabled_hint'),
              onExecute: async (selected: InventoryProduct[]) => {
                const ids = selected.map(s => s.product_id).filter(id => id !== undefined && id !== null);
                try {
                  confirmProducts({
                    params: {
                      path: {
                        model: 'product.product',
                      },
                    },
                    body: ids,
                  });
                  toast.success(t('success.confirm_product_success'));
                } catch (err: any) {
                  toast.error(`${t('errors.confirm_product_failed')}: ${err.detail}`, { autoClose: 5000 });
                }
              },
              confirmSettings: {
                renderTitle: selected => {
                  if (selected.length > 1) {
                    return t('general.confirm_products_title');
                  }
                  return t('general.confirm_product_title');
                },
                renderContent: selected => {
                  if (selected.length > 1) {
                    return t('general.confirm_products_text');
                  }
                  return t('general.confirm_product_text');
                },
                confirmLabel: t('general.confirm'),
                cancelLabel: t('general.cancel'),
              },
            },
          ]}
          kpiModel="product.product"
          columns={columns}
          useDataQuery={useStockInventoryQuery}
        />
      </div>
    </OverlayNavProvider>
  );
}

export default Inventory;
